/**
 * Download a file from an endpoint that requires a bearer token.
 *
 * The attachment endpoints are authenticated, so a plain link or an <a href>
 * would 401 — the browser does not send the Authorization header. The file has
 * to be fetched with the token and saved from a blob instead.
 *
 * Shared by AttachmentsModal and the form protocol's `download` field so the
 * two cannot drift apart.
 */
export async function downloadAuthenticatedFile({ url, filename, getAccessToken }) {
  const token = await getAccessToken()
  if (!token) {
    throw new Error('No authentication token available')
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })

  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`)
  }

  const blob = await response.blob()
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename || 'download'
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobUrl)
}

/**
 * Join the configured API base with a relative attachment path.
 *
 * The backend supplies paths like `/tickets/11066/attachments/<id>/download`
 * because FormRenderer has no access to the ticket.
 */
export function buildAttachmentUrl(baseUrl, path) {
  const base = (baseUrl || '/api/v1').replace(/\/+$/, '')
  const suffix = String(path || '')
  return suffix.startsWith('/') ? `${base}${suffix}` : `${base}/${suffix}`
}
